import React from 'react';
import './TermsAndConditions.css';
import PageHero from '../components/PageHero';

const TermsAndConditions = () => {
  return (
    <div className="terms-page-root">
      <PageHero
        title="Payment Terms & Conditions"
        subtitle="The complete framework governing projects, payments, and services at The Video Company (TVC)."
        align="left"
      />

      <section className="terms-and-conditions-container">
        <div className="terms-header">
          <p className="last-updated">Last updated: December 11, 2025</p>
          <p className="terms-intro">
            These terms and conditions outline the payment structure, project scope, responsibilities, and legal framework for all video production projects handled by The Video Company – TVC. By initiating a project with TVC, the Client agrees to all terms listed below.
          </p>
        </div>

        {/* Section 1 */}
        <div className="terms-section">
          <h2>1. Project Confirmation & Advance Payment</h2>
          <ul className="terms-list">
            <li>A project is considered confirmed only when the Client pays a <strong>50% advance</strong> of the total approved quotation to The Video Company – TVC.</li>
            <li>The Video Company – TVC will not block shoot dates, crew, or equipment until the advance is received.</li>
            <li>All dates are scheduled on a <strong>first-come, first-served basis.</strong></li>
            <li>Any delay from the Client in paying the advance may shift production timelines.</li>
          </ul>
        </div>

        {/* Section 2 */}
        <div className="terms-section">
          <h2>2. Payment Milestones</h2>
          <p>The Client agrees to follow the below payment structure for all projects handled by The Video Company – TVC:</p>
          <div className="payment-milestones">
            <div className="milestone">
              <h4>50% – Advance Payment</h4>
              <p>Paid by the Client to initiate pre-production, planning, and bookings.</p>
            </div>
            <div className="milestone">
              <h4>40% – After Shoot Completion</h4>
              <p>Payable by the Client before editing and post-production begins.</p>
            </div>
            <div className="milestone">
              <h4>10% – Before Final Delivery</h4>
              <p>The Client must clear the remaining amount for The Video Company – TVC to release final deliverables.</p>
            </div>
          </div>
          <p className="important-note">
            <strong>The Video Company – TVC reserves the right to withhold deliverables until all dues are cleared.</strong>
          </p>
        </div>

        {/* Section 3 */}
        <div className="terms-section">
          <h2>3. Modes of Payment</h2>
          <p>The Client may pay The Video Company – TVC through:</p>
          <ul className="terms-list">
            <li>Bank Transfer (NEFT/RTGS)</li>
            <li>UPI</li>
            <li>Online Payment Gateway</li>
          </ul>
          <p><em>Any transaction fee, payment gateway charge, or international transfer cost will be borne solely by the Client.</em></p>
        </div>

        {/* Section 4 */}
        <div className="terms-section">
          <h2>4. Scope of Work – Inclusions & Exclusions</h2>
          <p>The scope of work shared by The Video Company – TVC in the quotation will be followed strictly.</p>
          
          <h4>Included (as per the quotation):</h4>
          <ul className="terms-list">
            <li>Pre-production planning</li>
            <li>Crew charges</li>
            <li>Equipment charges</li>
            <li>Editing & post-production as specified</li>
            <li>Basic color grading and audio design</li>
          </ul>

          <h4>Not Included (unless specifically mentioned by TVC):</h4>
          <ul className="terms-list">
            <li>Talent/actor/artist fees</li>
            <li>Location permissions or rentals</li>
            <li>Props or set design</li>
            <li>Travel, food, stay, logistics</li>
            <li>Voice-over artist fees</li>
            <li>Special VFX, 3D animation, motion graphics</li>
            <li>Additional equipment requested later by the Client</li>
          </ul>

          <p><em>Any extra requirements from the Client will be billed separately by The Video Company – TVC.</em></p>
        </div>

        {/* Section 5 */}
        <div className="terms-section">
          <h2>5. Revisions & Change Requests</h2>
          <ul className="terms-list">
            <li>The Client is eligible for <strong>two rounds of revisions</strong> for each video.</li>
            <li>Additional revisions requested by the Client beyond this will incur extra charges.</li>
            <li>Any new creative direction or change requested by the Client after production or while editing will be considered additional scope and billed accordingly.</li>
            <li>Changes requested after final approval will also be chargeable.</li>
          </ul>
        </div>

        {/* Section 6 */}
        <div className="terms-section">
          <h2>6. Cancellation & Refund Policy</h2>
          <p>For all TVC video production projects, the Client agrees to the following payment and cancellation terms:</p>
          <ul className="terms-list">
            <li>All cancellation requests must be submitted in writing via email.</li>
            <li><strong>More than 2 months before production:</strong> The booking deposit will be <strong>fully refunded.</strong></li>
            <li><strong>Between 2 months and 1 week before production:</strong> The booking deposit will be <strong>non-refundable</strong> and retained by The Video Company (TVC).</li>
            <li><strong>Less than 1 week before production:</strong> Both the booking deposit and production payment will be <strong>fully retained by TVC.</strong></li>
            <li><strong>If TVC cancels:</strong> The Client will receive a <strong>full refund</strong> of all payments made, which represents the maximum extent of TVC's liability.</li>
          </ul>
        </div>

        {/* Section 7 */}
        <div className="terms-section">
          <h2>7. Shoot Day Terms & Responsibilities</h2>
          <p><strong>The Client must ensure:</strong></p>
          <ul className="terms-list">
            <li>Location permissions are arranged</li>
            <li>Talents/artists are present and on time</li>
            <li>The location is ready for shoot</li>
            <li>Brand guidelines, references, and scripts are shared in advance</li>
          </ul>

          <p><strong>Delays caused by the Client may lead to:</strong></p>
          <ul className="terms-list">
            <li>Overtime charges (after 8 hours of shoot)</li>
            <li>Additional equipment/crew costs</li>
            <li>Reshoot day charges, if required</li>
          </ul>

          <p><em>The Video Company – TVC will not be responsible for delays caused client-side.</em></p>
        </div>

        {/* Section 8 */}
        <div className="terms-section">
          <h2>8. Delivery Timelines</h2>
          <ul className="terms-list">
            <li>Rough cut will be delivered within <strong>5–10 working days</strong> from the shoot date by The Video Company – TVC, unless otherwise mentioned.</li>
            <li>Final timeline depends heavily on the Client's feedback speed.</li>
            <li>Delayed responses from the Client may extend the overall delivery schedule.</li>
          </ul>
        </div>

        {/* Section 9 */}
        <div className="terms-section">
          <h2>9. Usage Rights & Ownership</h2>
          <ul className="terms-list">
            <li>The Client will receive content usage rights only for the platforms specified in the quotation (e.g., social media, digital ads, internal use, broadcast).</li>
            <li>Any additional usage or paid media amplification requires separate licensing from The Video Company – TVC.</li>
            <li>All raw footage and working project files remain the intellectual property of The Video Company – TVC, unless purchased separately.</li>
            <li>Raw files will be shared only at an additional cost.</li>
            <li><strong>The Video Company – TVC holds the right to use final videos, BTS (behind-the-scenes) footage, and project visuals for its own portfolio, website showcase, marketing materials, and social media promotions.</strong></li>
          </ul>
        </div>

        {/* Section 10 */}
        <div className="terms-section">
          <h2>10. File Storage & Archiving</h2>
          <ul className="terms-list">
            <li><strong>The Video Company – TVC will provide 45–60 days of cloud backup</strong> for all completed projects.</li>
            <li><strong>TVC will also maintain an offline backup for up to 15 months,</strong> subject to storage capacity and internal archiving cycle.</li>
            <li>Long-term archival beyond these periods can be provided at an additional storage/maintenance fee.</li>
            <li>After the backup period ends, The Video Company – TVC is not responsible for retrieving or maintaining any project files.</li>
          </ul>
        </div>

        {/* Section 11 */}
        <div className="terms-section">
          <h2>11. Taxes</h2>
          <p>All payments made by the Client will be subject to <strong>18% GST,</strong> unless stated otherwise in the quotation.</p>
        </div>

        {/* Section 12 */}
        <div className="terms-section">
          <h2>12. Client Responsibilities</h2>
          <p>The Client agrees to:</p>
          <ul className="terms-list">
            <li>Provide accurate information, branding, and references</li>
            <li>Respond promptly for approvals and feedback</li>
            <li>Ensure all copyright content used is legally permitted</li>
            <li>Accept responsibility for legal issues arising from scripts, claims, or brand statements</li>
          </ul>
          <p><em>The Video Company – TVC will not be liable for errors caused due to incorrect information shared by the Client.</em></p>
        </div>

        {/* Section 13 */}
        <div className="terms-section">
          <h2>13. Limitation of Liability</h2>
          <ul className="terms-list">
            <li>The Video Company – TVC will not be responsible for delays caused by factors such as weather, technical failures, location issues, or talent delays.</li>
            <li>TVC is not liable for indirect or consequential damages resulting from the Client's use of the video.</li>
          </ul>
        </div>

        {/* Section 14 */}
        <div className="terms-section">
          <h2>14. Acceptance of Terms</h2>
          <p className="acceptance-text">
            By initiating the project and paying the advance, the Client acknowledges and agrees to all Payment Terms & Conditions listed above by The Video Company – TVC.
          </p>
        </div>

        {/* Contact Section */}
        <div className="terms-section contact-section">
          <h2>Questions or Clarifications?</h2>
          <p>
            If you have any questions about these Terms and Conditions, please contact us:
          </p>
          <ul className="contact-list">
            <li><strong>Email:</strong> connect@thevideocompany.in</li>
            <li><strong>Phone:</strong> +91 7411611528</li>
            <li><strong>Hours:</strong> Monday – Friday, 9 AM – 5 PM IST</li>
          </ul>
        </div>
      </section>
    </div>
  );
};

export default TermsAndConditions;
